import React, { useMemo } from 'react'
import {
  Card,
  ContentSection,
  FormSection,
  Heading,
  Layout,
  Paragraph,
  SectionHeading,
  TileSelect,
} from '@vwfs-bronson/bronson-react'
import { useTranslation } from 'react-i18next'
import { formatPrice } from '../../../services/common/format'
import { isVtiGiS } from '../../../services/common/utils'
import {
  getServiceAndInspection,
  setServiceAndInspection,
} from '../../../services/redux/features/custom.redux'
import { InfoTooltip } from '../../../components/InfoTooltip'
import { useDispatch, useSelector } from 'react-redux'
import { getStorefrontData } from '../../../services/redux/features/storefront.redux'
import { useAriaLabel } from '../../../hooks/useAriaLabel'

const AdditionalProducts = ({ onSelection }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const storefrontData = useSelector(getStorefrontData)
  const serviceAndInspection = useSelector(getServiceAndInspection)

  const { ariaLabel, setAriaLabelStatus } = useAriaLabel()

  const additionalProducts = useMemo(
    () => storefrontData?.financialProduct?.additionalProducts,
    [storefrontData]
  )

  const shouldShowAdditionalProducts =
    isVtiGiS(storefrontData) &&
    additionalProducts?.find((product) => product.isVisible)
  return (
    <>
      <ContentSection pageWrap className="u-pt-small u-pb-small">
        <ContentSection.ComponentWrapper>
          <Card
            title={
              <Heading level={2} className="u-text-left">
                {t('additionalProducts:title')}
              </Heading>
            }
          >
            {(
              t('additionalProducts:texts', {
                returnObjects: true,
              }) as Array<string>
            )?.map((text: string) => (
              <Paragraph className="u-text-left" key={text}>
                {text}
              </Paragraph>
            ))}
            <Layout center equalHeight>
              <Layout.Item default="1/3" s="1/1">
                <TileSelect
                  id="serviceAndInspection"
                  title={t('additionalProducts:serviceAndInspection.title')}
                  headingLevel={3}
                  checked={serviceAndInspection}
                  disabled={!storefrontData?.serviceInspectionRate}
                  onClick={() => {
                    dispatch(setServiceAndInspection(!serviceAndInspection))
                  }}
                >
                  {storefrontData?.serviceInspectionRate ? (
                    <>
                      <Paragraph>
                        {formatPrice(
                          storefrontData?.serviceInspectionRate?.prices?.[0]
                            ?.grossLoanAmount?.amount || 0
                        )}
                        {t('additionalProducts:serviceAndInspection.text1')}
                        <InfoTooltip
                          label={t(
                            'additionalProducts:serviceAndInspection.title'
                          )}
                          text={t(
                            'additionalProducts:serviceAndInspection.tooltip'
                          )}
                        />
                      </Paragraph>
                      <Paragraph>
                        {t('additionalProducts:serviceAndInspection.text2')}
                      </Paragraph>
                    </>
                  ) : (
                    <Paragraph>
                      {t('additionalProducts:serviceAndInspection.disabled')}
                    </Paragraph>
                  )}
                </TileSelect>
              </Layout.Item>
              {!!shouldShowAdditionalProducts &&
                additionalProducts?.map((product: any) =>
                  product?.isVisible ? (
                    <Layout.Item default="1/3" s="1/1" key={product?.name}>
                      <TileSelect
                        id={product?.name}
                        title={product?.name}
                        headingLevel={3}
                        onClick={() => onSelection(product)}
                        checked={product?.isSelected}
                        disabled={!product?.isSelectable}
                      >
                        <Paragraph>{product?.description}</Paragraph>
                        <Paragraph>{product?.disclaimer}</Paragraph>
                      </TileSelect>
                    </Layout.Item>
                  ) : (
                    <></>
                  )
                )}
            </Layout>
          </Card>
        </ContentSection.ComponentWrapper>
      </ContentSection>
      <ContentSection pageWrap className="u-pt-small u-pb-small">
        <ContentSection.ComponentWrapper>
          <FormSection
            title={
              <SectionHeading level={3}>
                {t('additionalProducts:accordion.title')}
              </SectionHeading>
            }
            onChange={setAriaLabelStatus}
            aria-label={ariaLabel}
          >
            <Paragraph>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('additionalProducts:accordion.text1'),
                }}
              ></span>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={t('additionalProducts:accordion.linkHref')}
              >
                {t('additionalProducts:accordion.linkText')}
              </a>
            </Paragraph>
            <Paragraph
              dangerouslySetInnerHTML={{
                __html: t('additionalProducts:accordion.text2'),
              }}
            />
            <Paragraph
              dangerouslySetInnerHTML={{
                __html: t('additionalProducts:accordion.text3'),
              }}
            />
            <Paragraph
              dangerouslySetInnerHTML={{
                __html: t('additionalProducts:accordion.text4'),
              }}
            />
          </FormSection>
        </ContentSection.ComponentWrapper>
      </ContentSection>
    </>
  )
}

export default AdditionalProducts
